import { accountPost, prepareAccountSecurity } from '../api/account';
import { fetchAccountSessionMe } from '../api/accountSession';
import { PublicApiClient } from '../api/client';
import { createPublicTranslator } from '../core/i18n';
import { ACCOUNT_I18N_BUNDLE, type AccountMessageKey } from '../i18n/account';
import {
  bindAccountAuthSecretToggles,
  bindAccountPasswordGenerators,
  bindAccountRecoverySecretActions,
  renderAccountAuthCheckbox,
  renderAccountAuthField,
  renderAccountPasswordGenerator,
  renderAccountRecoverySecretBox,
  type AccountAuthFieldOptions,
} from '../account/authFields';
import {
  clearAccountHandleSuggestions,
  ensureAccountHandleAvailable,
} from '../account/handleSuggestions';
import {
  ACCOUNT_PSEUDONYM_MAX_LENGTH,
  ACCOUNT_PSEUDONYM_MIN_LENGTH,
  ACCOUNT_PSEUDONYM_PATTERN,
  validateAccountPseudonym,
  type AccountPseudonymValidationCode,
} from '../account/pseudonymPolicy';

export type HeaderAuthController = {
  destroy(): void;
};

export type HeaderAuthOptions = {
  doc: Document;
  apiBase: string;
  signal: AbortSignal;
};

type HeaderAuthTarget = HTMLAnchorElement & {
  dataset: DOMStringMap & {
    authLoginLabel?: string;
    authAccountLabel?: string;
    authLoginHref?: string;
    authAccountHref?: string;
  };
};

type AccountState = 'guest' | 'signed-in';
type AuthView = 'login' | 'register' | 'recover';

type HeaderAuthRuntime = {
  doc: Document;
  api: PublicApiClient;
  signal: AbortSignal;
  targets: HeaderAuthTarget[];
  header: HTMLElement | null;
  state: AccountState;
  lang: string;
  translator: ReturnType<typeof createPublicTranslator<AccountMessageKey>>;
  listeners: Array<() => void>;
  overlay: HTMLElement | null;
  overlayCleanup: Array<() => void>;
  clientStartedAt: number;
  accountDisplayName: string;
};

const ACCOUNT_ICON_SPRITE_PATH = '/assets/icons/quaom-account-icons.svg';
const ACCOUNT_PASSWORD_MIN_LENGTH = 10;
const ACCOUNT_PASSWORD_MAX_LENGTH = 32;
const ACCOUNT_PASSWORD_PATTERN = '^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])[A-Za-z0-9]{10,32}$';

type AccountPasswordValidationCode =
  | 'ACCOUNT_PASSWORD_REQUIRED'
  | 'ACCOUNT_PASSWORD_INVALID_LENGTH'
  | 'ACCOUNT_PASSWORD_LATIN_ALNUM_ONLY'
  | 'ACCOUNT_PASSWORD_MISSING_LOWERCASE'
  | 'ACCOUNT_PASSWORD_MISSING_UPPERCASE'
  | 'ACCOUNT_PASSWORD_MISSING_DIGIT';

export function mountHeaderAuth(options: HeaderAuthOptions): HeaderAuthController {
  const targets = Array.from(
    options.doc.querySelectorAll<HeaderAuthTarget>('[data-header-auth], [data-header-auth-mobile]'),
  );
  const header = options.doc.querySelector<HTMLElement>('[data-header-account-state]');
  if (!targets.length) return { destroy() {} };

  const lang = options.doc.documentElement.lang || 'en';
  const runtime: HeaderAuthRuntime = {
    doc: options.doc,
    api: new PublicApiClient({ baseUrl: options.apiBase, signal: options.signal }),
    signal: options.signal,
    targets,
    header,
    state: 'guest',
    lang,
    translator: createPublicTranslator(ACCOUNT_I18N_BUNDLE, lang),
    listeners: [],
    overlay: null,
    overlayCleanup: [],
    clientStartedAt: Date.now(),
    accountDisplayName: '',
  };

  let destroyed = false;
  setAccountState(runtime, 'guest');

  for (const target of targets) {
    const onClick = (event: MouseEvent) => {
      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      )
        return;
      if (runtime.state === 'signed-in') return;
      event.preventDefault();
      openAuthOverlay(runtime, target.dataset.authLoginHref || '/account/login/');
    };
    target.addEventListener('click', onClick);
    runtime.listeners.push(() => target.removeEventListener('click', onClick));
  }

  fetchAccountSessionMe({ apiBase: options.apiBase, abortSignal: options.signal })
    .then((session) => {
      if (destroyed) return;
      setAccountState(
        runtime,
        session?.user ? 'signed-in' : 'guest',
        session?.user?.handle || session?.user?.pseudonym || '',
      );
    })
    .catch(() => {
      if (destroyed) return;
      setAccountState(runtime, 'guest');
    });

  return {
    destroy() {
      destroyed = true;
      for (const remove of runtime.listeners.splice(0)) remove();
      closeAuthOverlay(runtime);
    },
  };
}

function setAccountState(
  runtime: HeaderAuthRuntime,
  state: AccountState,
  accountDisplayName = '',
): void {
  runtime.state = state;
  runtime.accountDisplayName =
    state === 'signed-in'
      ? String(accountDisplayName || runtime.accountDisplayName || '').trim()
      : '';
  runtime.doc.documentElement.dataset.accountState = state;
  if (runtime.doc.body) runtime.doc.body.dataset.accountState = state;
  if (runtime.header) runtime.header.dataset.headerAccountState = state;
  setTargets(runtime.targets, state, runtime.accountDisplayName);
}

function setTargets(
  targets: HeaderAuthTarget[],
  state: AccountState,
  accountDisplayName = '',
): void {
  for (const target of targets) {
    const loginLabel = target.dataset.authLoginLabel || 'Sign in';
    const accountLabel = target.dataset.authAccountLabel || 'Account';
    const loginHref = withReturnTo(target.dataset.authLoginHref || '/account/login/');
    const accountHref = target.dataset.authAccountHref || '/account/';
    const displayName = String(accountDisplayName || accountLabel).trim() || accountLabel;

    if (state === 'signed-in') {
      renderSignedInAccountTarget(target, displayName);
    } else {
      target.textContent = loginLabel;
      target.removeAttribute('title');
    }

    target.href = state === 'signed-in' ? accountHref : loginHref;
    target.dataset.accountState = state;
    target.setAttribute(
      'aria-label',
      state === 'signed-in' ? `${accountLabel}: ${displayName}` : loginLabel,
    );
    if (state === 'signed-in') {
      target.removeAttribute('aria-haspopup');
      target.removeAttribute('aria-expanded');
    } else {
      target.setAttribute('aria-haspopup', 'dialog');
    }
  }
}

function renderSignedInAccountTarget(target: HeaderAuthTarget, displayName: string): void {
  const doc = target.ownerDocument;
  const iconWrap = doc.createElement('span');
  iconWrap.className = 'header-account-icon-wrap';
  iconWrap.setAttribute('aria-hidden', 'true');

  const icon = doc.createElementNS('http://www.w3.org/2000/svg', 'svg');
  icon.setAttribute('class', 'header-account-icon');
  icon.setAttribute('width', '18');
  icon.setAttribute('height', '18');
  icon.setAttribute('focusable', 'false');

  const use = doc.createElementNS('http://www.w3.org/2000/svg', 'use');
  use.setAttribute('href', `${ACCOUNT_ICON_SPRITE_PATH}#qe-icon-user`);
  icon.appendChild(use);
  iconWrap.appendChild(icon);

  const label = doc.createElement('span');
  label.className = 'header-auth-label';
  label.textContent = displayName;

  target.replaceChildren(iconWrap, label);
  target.title = displayName;
}

function openAuthOverlay(
  runtime: HeaderAuthRuntime,
  loginHref: string,
  initialView: AuthView = 'login',
): void {
  if (runtime.overlay) {
    renderAuthOverlayView(runtime, initialView, loginHref);
    runtime.overlay.querySelector<HTMLInputElement>('input[name="pseudonym"]')?.focus();
    return;
  }

  const t = runtime.translator.t;
  const overlay = runtime.doc.createElement('div');
  overlay.className = 'header-auth-overlay';
  overlay.innerHTML = [
    '<div class="header-auth-backdrop" data-header-auth-close></div>',
    `<section class="header-auth-dialog" role="dialog" aria-modal="true" aria-labelledby="header-auth-title">`,
    `<button class="header-auth-close" type="button" data-header-auth-close aria-label="${escapeAttr(t('close'))}">${headerIcon('x')}</button>`,
    '<div data-header-auth-content></div>',
    '</section>',
  ].join('');
  runtime.doc.body?.appendChild(overlay);
  runtime.overlay = overlay;
  runtime.doc.body?.classList.add('has-header-auth-overlay');

  const close = () => closeAuthOverlay(runtime);
  overlay.querySelectorAll('[data-header-auth-close]').forEach((node) => {
    node.addEventListener('click', close);
    runtime.overlayCleanup.push(() => node.removeEventListener('click', close));
  });
  const onKeydown = (event: KeyboardEvent) => {
    if (event.key === 'Escape') closeAuthOverlay(runtime);
  };
  runtime.doc.addEventListener('keydown', onKeydown);
  runtime.overlayCleanup.push(() => runtime.doc.removeEventListener('keydown', onKeydown));

  renderAuthOverlayView(runtime, initialView, loginHref);
  window.setTimeout(
    () => overlay.querySelector<HTMLInputElement>('input[name="pseudonym"]')?.focus(),
    0,
  );
}

const AUTH_SHORT_TITLE_OVERRIDES: Record<string, Partial<Record<AuthView, string>>> = {
  uk: { register: 'Створити', recover: 'Відновити' },
  ru: { register: 'Создать', recover: 'Восстановить' },
};

const AUTH_FIELD_PLACEHOLDERS: Record<string, { pseudonym: string; password: string }> = {
  uk: { pseudonym: '6-20 символів', password: '10–32 символи' },
  ru: { pseudonym: '6-20 символов', password: '10–32 символа' },
};

const AUTH_GENERATE_LABELS: Record<string, string> = {
  uk: 'Згенерувати',
  ru: 'Сгенерировать',
};

const AUTH_OPEN_ACCOUNT_LABELS: Record<string, string> = {
  uk: 'Відкрити акаунт',
  ru: 'Открыть аккаунт',
};

function authShortTitle(lang: string, view: AuthView, fallback: string): string {
  return AUTH_SHORT_TITLE_OVERRIDES[lang]?.[view] || fallback;
}

function authFieldPlaceholder(lang: string, kind: 'pseudonym' | 'password'): string {
  return AUTH_FIELD_PLACEHOLDERS[lang]?.[kind] || '';
}

function authGenerateLabel(lang: string, fallback: string): string {
  return AUTH_GENERATE_LABELS[lang] || fallback;
}

function authOpenAccountLabel(lang: string, fallback: string): string {
  return AUTH_OPEN_ACCOUNT_LABELS[lang] || fallback;
}

function localizedAccountHref(lang: string): string {
  return lang && lang !== 'en' ? `/${lang}/account/` : '/account/';
}

function renderAuthOverlayView(
  runtime: HeaderAuthRuntime,
  view: AuthView,
  loginHref: string,
): void {
  const content = runtime.overlay?.querySelector<HTMLElement>('[data-header-auth-content]');
  if (!content) return;
  const t = runtime.translator.t;
  const title = authShortTitle(
    runtime.lang,
    view,
    view === 'register' ? t('register') : view === 'recover' ? t('recover') : t('login'),
  );
  const actionLabel =
    view === 'register' ? t('register') : view === 'recover' ? t('completeRecovery') : t('login');
  const formName = `header-auth-${view}-form`;

  content.innerHTML = [
    `<header class="header-auth-dialog__header"><p class="header-auth-eyebrow">${escapeHtml(t('eyebrow'))}</p><h2 id="header-auth-title">${escapeHtml(title)}</h2></header>`,
    `<form class="header-auth-form" data-header-auth-form="${escapeAttr(formName)}" autocomplete="off">`,
    renderHeaderAuthControls(view, runtime.lang, t),
    '<label class="header-auth-hp" aria-hidden="true" tabindex="-1"><span>Website</span><input name="website" tabindex="-1" autocomplete="off"></label>',
    `<button class="header-auth-submit" type="submit">${escapeHtml(actionLabel)}</button>`,
    `<p class="header-auth-status" data-header-auth-status></p>`,
    '</form>',
    renderHeaderAuthLinks(view, loginHref, t),
  ].join('');

  content.querySelectorAll<HTMLAnchorElement>('[data-header-auth-view]').forEach((link) => {
    link.addEventListener('click', (event) => {
      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      )
        return;
      const next = normalizeAuthView(link.dataset.headerAuthView || 'login');
      event.preventDefault();
      renderAuthOverlayView(runtime, next, loginHref);
      window.setTimeout(
        () => runtime.overlay?.querySelector<HTMLInputElement>('input[name="pseudonym"]')?.focus(),
        0,
      );
    });
  });

  bindAccountAuthSecretToggles(content);
  bindAccountPasswordGenerators(content);

  const form = content.querySelector<HTMLFormElement>('[data-header-auth-form]');
  const status = content.querySelector<HTMLElement>('[data-header-auth-status]');
  if (!form) return;
  bindHeaderAuthValidation(form, view, runtime);
  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    if (runtime.signal.aborted) return;
    if (!syncHeaderAuthValidation(form, view, runtime) || !form.reportValidity()) return;
    setHeaderAuthStatus(status, '');
    setHeaderAuthBusy(form, true);
    try {
      if (view === 'register') {
        await submitHeaderRegister(runtime, form, content);
        return;
      }
      if (view === 'recover') {
        await submitHeaderRecover(runtime, form, content);
        return;
      }
      await submitHeaderLogin(runtime, form);
    } catch (error) {
      setHeaderAuthStatus(status, accountErrorMessage(error, runtime.translator.t));
    } finally {
      setHeaderAuthBusy(form, false);
    }
  });
}

function renderHeaderAuthControls(
  view: AuthView,
  lang: string,
  t: ReturnType<typeof createPublicTranslator<AccountMessageKey>>['t'],
): string {
  if (view === 'register') {
    return [
      headerField('register', 'pseudonym', t('pseudonym'), 'text', 'username', {
        spellcheck: false,
        autocapitalize: 'none',
        minLength: ACCOUNT_PSEUDONYM_MIN_LENGTH,
        maxLength: ACCOUNT_PSEUDONYM_MAX_LENGTH,
        pattern: ACCOUNT_PSEUDONYM_PATTERN,
        placeholder: authFieldPlaceholder(lang, 'pseudonym'),
      }),
      headerField('register', 'password', t('password'), 'password', 'new-password', {
        minLength: ACCOUNT_PASSWORD_MIN_LENGTH,
        maxLength: ACCOUNT_PASSWORD_MAX_LENGTH,
        pattern: ACCOUNT_PASSWORD_PATTERN,
        placeholder: authFieldPlaceholder(lang, 'password'),
        labelAction: renderAccountPasswordGenerator(
          authGenerateLabel(lang, t('generatePassword')),
          'password',
        ),
        secretToggle: true,
        secretShowLabel: t('showPassword'),
        secretHideLabel: t('hidePassword'),
      }),
      headerCheckbox('register', 'createRecoveryKey', t('createRecoveryKey'), true),
      `<p class="header-auth-warning" data-header-auth-recovery-warning hidden>${escapeHtml(t('recoveryKeyWarning'))}</p>`,
    ].join('');
  }
  if (view === 'recover') {
    return [
      headerField('recover', 'pseudonym', t('pseudonym'), 'text', 'username', {
        spellcheck: false,
        autocapitalize: 'none',
      }),
      headerField('recover', 'recoveryKey', t('recoveryKey'), 'text', 'one-time-code', {
        spellcheck: false,
        autocapitalize: 'none',
      }),
      `<p class="header-auth-hint">${escapeHtml(t('recoveryKeyRotatedAfterRecovery'))}</p>`,
      headerField('recover', 'newPassword', t('newPassword'), 'password', 'new-password', {
        minLength: ACCOUNT_PASSWORD_MIN_LENGTH,
        maxLength: ACCOUNT_PASSWORD_MAX_LENGTH,
        pattern: ACCOUNT_PASSWORD_PATTERN,
        labelAction: renderAccountPasswordGenerator(
          authGenerateLabel(lang, t('generatePassword')),
          'newPassword',
        ),
        secretToggle: true,
        secretShowLabel: t('showPassword'),
        secretHideLabel: t('hidePassword'),
      }),
    ].join('');
  }
  return [
    headerField('login', 'pseudonym', t('pseudonym'), 'text', 'username', {
      spellcheck: false,
      autocapitalize: 'none',
    }),
    headerField('login', 'password', t('password'), 'password', 'current-password', {
      secretToggle: true,
      secretShowLabel: t('showPassword'),
      secretHideLabel: t('hidePassword'),
    }),
  ].join('');
}

function renderHeaderAuthLinks(
  view: AuthView,
  loginHref: string,
  t: ReturnType<typeof createPublicTranslator<AccountMessageKey>>['t'],
): string {
  const items: string[] = [];
  if (view !== 'login')
    items.push(
      authViewLink('login', withReturnTo(localizedSiblingHref(loginHref, 'login')), t('login')),
    );
  if (view !== 'register')
    items.push(
      authViewLink(
        'register',
        withReturnTo(localizedSiblingHref(loginHref, 'register')),
        t('register'),
      ),
    );
  if (view !== 'recover')
    items.push(authViewLink('recover', localizedSiblingHref(loginHref, 'recover'), t('recover')));
  return `<nav class="header-auth-links" aria-label="${escapeAttr(t('eyebrow'))}">${items.join('<span class="header-auth-separator" aria-hidden="true">|</span>')}</nav>`;
}

function authViewLink(view: AuthView, href: string, label: string): string {
  return `<a href="${escapeAttr(href)}" data-header-auth-view="${escapeAttr(view)}">${escapeHtml(label)}</a>`;
}

async function submitHeaderLogin(runtime: HeaderAuthRuntime, form: HTMLFormElement): Promise<void> {
  const security = await prepareAccountSecurity(runtime.api, 'login', runtime.clientStartedAt, {
    honeypot: readFormValue(form, 'website'),
    signal: runtime.signal,
  });
  await accountPost(runtime.api, '/api/account/login', {
    pseudonym: readFormValue(form, 'pseudonym'),
    password: readFormValue(form, 'password'),
    ...security,
  });
  setAccountState(runtime, 'signed-in', readFormValue(form, 'pseudonym'));
  closeAuthOverlay(runtime);
}

async function submitHeaderRegister(
  runtime: HeaderAuthRuntime,
  form: HTMLFormElement,
  content: HTMLElement,
): Promise<void> {
  const pseudonym = validateAccountPseudonym(readFormValue(form, 'pseudonym'));
  if (!pseudonym.ok)
    throw accountMessageError(translatePseudonymPolicyCode(pseudonym.code, runtime.translator));
  const pseudonymInput = form.querySelector<HTMLInputElement>('input[name="pseudonym"]');
  if (
    !(await ensureAccountHandleAvailable(
      runtime.api,
      pseudonymInput,
      runtime.translator,
      runtime.signal,
    ))
  ) {
    throw accountMessageError(runtime.translator.t('handleTaken'));
  }
  const password = validateAccountPassword(readFormValue(form, 'password'));
  if (!password.ok)
    throw accountMessageError(translatePasswordPolicyCode(password.code, runtime.translator));
  const security = await prepareAccountSecurity(runtime.api, 'register', runtime.clientStartedAt, {
    honeypot: readFormValue(form, 'website'),
    signal: runtime.signal,
  });
  const result = await accountPost<{ recoveryKey?: string }>(runtime.api, '/api/account/register', {
    pseudonym: pseudonym.pseudonym,
    password: password.password,
    createRecoveryKey:
      (form.elements.namedItem('createRecoveryKey') as HTMLInputElement | null)?.checked === true,
    ...security,
  });
  content.innerHTML = headerAuthResult(
    runtime.lang,
    runtime.translator.t('register'),
    runtime.translator.t('success'),
    result.recoveryKey,
    runtime.translator.t,
    pseudonym.pseudonym,
  );
  bindAccountRecoverySecretActions(content);
}

async function submitHeaderRecover(
  runtime: HeaderAuthRuntime,
  form: HTMLFormElement,
  content: HTMLElement,
): Promise<void> {
  const password = validateAccountPassword(readFormValue(form, 'newPassword'));
  if (!password.ok)
    throw accountMessageError(translatePasswordPolicyCode(password.code, runtime.translator));
  const security = await prepareAccountSecurity(
    runtime.api,
    'recover_key',
    runtime.clientStartedAt,
    {
      honeypot: readFormValue(form, 'website'),
      signal: runtime.signal,
    },
  );
  const result = await accountPost<{ recoveryKey?: string }>(
    runtime.api,
    '/api/account/recover/by-key',
    {
      pseudonym: readFormValue(form, 'pseudonym'),
      recoveryKey: readFormValue(form, 'recoveryKey'),
      newPassword: password.password,
      ...security,
    },
  );
  setAccountState(runtime, 'signed-in', readFormValue(form, 'pseudonym'));
  const secret = result.recoveryKey
    ? headerSecretBox(
        runtime.lang,
        runtime.translator.t('recoveryKey'),
        result.recoveryKey,
        runtime.translator.t,
        readFormValue(form, 'pseudonym'),
      )
    : '';
  content.innerHTML = [
    `<header class="header-auth-dialog__header"><p class="header-auth-eyebrow">${escapeHtml(runtime.translator.t('recover'))}</p><h2 id="header-auth-title">${escapeHtml(runtime.translator.t('success'))}</h2></header>`,
    `<p class="header-auth-hint">${escapeHtml(runtime.translator.t('recoveryKeyRotatedAfterRecovery'))}</p>`,
    secret ||
      `<div class="header-auth-result"><a class="account-button account-button--secondary" href="${escapeAttr(localizedAccountHref(runtime.lang))}">${escapeHtml(authOpenAccountLabel(runtime.lang, runtime.translator.t('open')))}</a></div>`,
  ].join('');
  bindAccountRecoverySecretActions(content);
}

function headerAuthResult(
  lang: string,
  title: string,
  message: string,
  recoveryKey: string | undefined,
  t: ReturnType<typeof createPublicTranslator<AccountMessageKey>>['t'],
  pseudonym = '',
): string {
  if (recoveryKey) {
    return `<div class="header-auth-result">${headerSecretBox(lang, t('recoveryKey'), recoveryKey, t, pseudonym)}</div>`;
  }
  return [
    `<header class="header-auth-dialog__header"><p class="header-auth-eyebrow">${escapeHtml(title)}</p><h2 id="header-auth-title">${escapeHtml(message)}</h2></header>`,
    '<div class="header-auth-result"></div>',
  ].join('');
}

function headerSecretBox(
  lang: string,
  label: string,
  secret: string,
  t: ReturnType<typeof createPublicTranslator<AccountMessageKey>>['t'],
  pseudonym = '',
): string {
  return renderAccountRecoverySecretBox({
    label,
    secret,
    pseudonym,
    warning: t('recoveryKeyWarning'),
    copyLabel: t('copy'),
    downloadLabel: t('download'),
    copiedLabel: t('copied'),
    downloadedLabel: t('downloaded'),
    pseudonymLabel: t('pseudonym'),
    recoveryKeyLabel: t('recoveryKey'),
    createdLabel: t('created'),
    fileWarning: t('recoveryFileWarning'),
    primaryActionLabel: authOpenAccountLabel(lang, t('open')),
    primaryActionHref: localizedAccountHref(lang),
  });
}

function bindHeaderAuthValidation(
  form: HTMLFormElement | null,
  view: AuthView,
  runtime: HeaderAuthRuntime,
): void {
  if (!form) return;
  const recoveryKey = form.querySelector<HTMLInputElement>('input[name="createRecoveryKey"]');
  const warning = form.querySelector<HTMLElement>('[data-header-auth-recovery-warning]');
  if (recoveryKey && warning) {
    const sync = () => {
      warning.hidden = recoveryKey.checked;
    };
    recoveryKey.addEventListener('change', sync);
    sync();
  }
  const passwordName = view === 'recover' ? 'newPassword' : view === 'register' ? 'password' : '';
  const passwordInput = passwordName
    ? form.querySelector<HTMLInputElement>(`input[name="${passwordName}"]`)
    : null;
  if (passwordInput) {
    const sync = () => {
      const password = validateAccountPassword(passwordInput.value);
      passwordInput.setCustomValidity(
        password.ok ? '' : translatePasswordPolicyCode(password.code, runtime.translator),
      );
    };
    passwordInput.addEventListener('input', sync);
    passwordInput.addEventListener('blur', sync);
  }
  const pseudonymInput =
    view === 'register' ? form.querySelector<HTMLInputElement>('input[name="pseudonym"]') : null;
  if (pseudonymInput) {
    const sync = () => {
      const pseudonym = validateAccountPseudonym(pseudonymInput.value);
      pseudonymInput.setCustomValidity(
        pseudonym.ok ? '' : translatePseudonymPolicyCode(pseudonym.code, runtime.translator),
      );
      clearAccountHandleSuggestions(pseudonymInput);
    };
    pseudonymInput.addEventListener('input', sync);
    pseudonymInput.addEventListener('blur', () => {
      sync();
      const pseudonym = validateAccountPseudonym(pseudonymInput.value);
      if (pseudonym.ok)
        void ensureAccountHandleAvailable(
          runtime.api,
          pseudonymInput,
          runtime.translator,
          runtime.signal,
        );
    });
  }
}

function syncHeaderAuthValidation(
  form: HTMLFormElement,
  view: AuthView,
  runtime: HeaderAuthRuntime,
): boolean {
  const messages: string[] = [];
  if (view === 'register') {
    const pseudonymInput = form.querySelector<HTMLInputElement>('input[name="pseudonym"]');
    if (pseudonymInput) {
      const pseudonym = validateAccountPseudonym(pseudonymInput.value);
      const message = pseudonym.ok
        ? ''
        : translatePseudonymPolicyCode(pseudonym.code, runtime.translator);
      pseudonymInput.setCustomValidity(message);
      if (message) messages.push(message);
    }
  }
  if (view === 'register' || view === 'recover') {
    const fieldName = view === 'recover' ? 'newPassword' : 'password';
    const passwordInput = form.querySelector<HTMLInputElement>(`input[name="${fieldName}"]`);
    if (passwordInput) {
      const password = validateAccountPassword(passwordInput.value);
      const message = password.ok
        ? ''
        : translatePasswordPolicyCode(password.code, runtime.translator);
      passwordInput.setCustomValidity(message);
      if (message) messages.push(message);
    }
  }
  const status = form.querySelector<HTMLElement>('[data-header-auth-status]');
  if (status && messages.length) setHeaderAuthStatus(status, messages[0]);
  return messages.length === 0;
}
function closeAuthOverlay(runtime: HeaderAuthRuntime): void {
  for (const remove of runtime.overlayCleanup.splice(0)) remove();
  runtime.overlay?.remove();
  runtime.overlay = null;
  runtime.doc.body?.classList.remove('has-header-auth-overlay');
}

function headerField(
  view: AuthView,
  name: string,
  label: string,
  type: string,
  autocomplete: string,
  options: AccountAuthFieldOptions = {},
): string {
  return renderAccountAuthField(name, label, type, {
    ...options,
    autocomplete,
    required: true,
    scope: `header-auth-${view}-${name}`,
    fieldClass: 'header-auth-field',
    hintClass: 'header-auth-hint',
  });
}

function headerCheckbox(view: AuthView, name: string, label: string, checked: boolean): string {
  return renderAccountAuthCheckbox(name, label, {
    checked,
    scope: `header-auth-${view}-${name}`,
    fieldClass: 'header-auth-field header-auth-field--inline',
  });
}

function headerIcon(name: string): string {
  const safeName = /^[a-z0-9-]+$/.test(name) ? name : 'circle-x';
  return `<svg class="header-auth-icon" width="18" height="18" aria-hidden="true" focusable="false"><use href="${ACCOUNT_ICON_SPRITE_PATH}#qe-icon-${safeName}"></use></svg>`;
}

function setHeaderAuthBusy(form: HTMLFormElement, busy: boolean): void {
  form.querySelectorAll<HTMLInputElement | HTMLButtonElement>('input, button').forEach((field) => {
    if (field.name === 'website') return;
    field.disabled = busy;
  });
}

function setHeaderAuthStatus(status: HTMLElement | null, message: string): void {
  if (!status) return;
  status.textContent = message;
  status.hidden = !message;
}

function accountErrorMessage(
  error: unknown,
  t: ReturnType<typeof createPublicTranslator<AccountMessageKey>>['t'],
): string {
  const rawMessage = String((error as { message?: unknown })?.message || '');
  if ((error as { name?: unknown })?.name === 'AccountMessageError' && rawMessage)
    return rawMessage;
  const code = String(
    (error as { code?: unknown; message?: unknown })?.code || rawMessage || '',
  ).toUpperCase();
  if (code.includes('INVALID_PASSWORD') || code.includes('INVALID_CREDENTIALS'))
    return t('invalidPassword');
  if (code.includes('NOT_FOUND') || code.includes('ACCOUNT_NOT_FOUND')) return t('accountNotFound');
  if (code.includes('RATE') || code.includes('TOO_MANY')) return t('tooManyAttempts');
  if (code.includes('FEATURE_DISABLED')) return t('featureDisabled');
  if (code.includes('ACCOUNT_HANDLE_TAKEN')) return t('handleTaken');
  if (code.includes('SECURITY_HOLD')) return t('securityHoldShort');
  return t('error');
}

function readFormValue(form: HTMLFormElement, name: string): string {
  const field = form.elements.namedItem(name);
  if (!field) return '';
  if (field instanceof RadioNodeList) return String(field.value || '');
  return String((field as HTMLInputElement).value || '').trim();
}

function withReturnTo(loginHref: string): string {
  const returnTo = currentReturnTo();
  if (!returnTo) return loginHref;
  try {
    const url = new URL(loginHref, window.location.origin);
    url.searchParams.set('returnTo', returnTo);
    return `${url.pathname}${url.search}`;
  } catch {
    const separator = loginHref.includes('?') ? '&' : '?';
    return `${loginHref}${separator}returnTo=${encodeURIComponent(returnTo)}`;
  }
}

function currentReturnTo(): string | null {
  const path = `${window.location.pathname}${window.location.search}`;
  if (!path || path === '/') return null;
  if (isAuthPath(window.location.pathname)) return null;
  if (!isSafeLocalPath(path)) return null;
  return path;
}

function isAuthPath(pathname: string): boolean {
  const normalized = pathname.replace(/\/+$/, '');
  return /\/(account\/login|account\/register|account\/recover)$/.test(normalized);
}

function isAccountPath(pathname: string): boolean {
  return /\/(account)(?:\/|$)/.test(pathname);
}

function localizedSiblingHref(loginHref: string, section: AuthView): string {
  try {
    const url = new URL(loginHref, window.location.origin);
    url.pathname = url.pathname.replace(
      /account\/(login|register|recover)\/?$/,
      `account/${section}/`,
    );
    return `${url.pathname}${url.search}`;
  } catch {
    return `/account/${section}/`;
  }
}

function normalizeAuthView(value: string): AuthView {
  return value === 'register' || value === 'recover' ? value : 'login';
}

function validateAccountPassword(
  value: unknown,
): { ok: true; password: string } | { ok: false; code: AccountPasswordValidationCode } {
  const password = String(value || '');
  if (!password) return { ok: false, code: 'ACCOUNT_PASSWORD_REQUIRED' };
  if (
    password.length < ACCOUNT_PASSWORD_MIN_LENGTH ||
    password.length > ACCOUNT_PASSWORD_MAX_LENGTH
  )
    return { ok: false, code: 'ACCOUNT_PASSWORD_INVALID_LENGTH' };
  if (!/^[A-Za-z0-9]+$/.test(password))
    return { ok: false, code: 'ACCOUNT_PASSWORD_LATIN_ALNUM_ONLY' };
  if (!/[a-z]/.test(password)) return { ok: false, code: 'ACCOUNT_PASSWORD_MISSING_LOWERCASE' };
  if (!/[A-Z]/.test(password)) return { ok: false, code: 'ACCOUNT_PASSWORD_MISSING_UPPERCASE' };
  if (!/[0-9]/.test(password)) return { ok: false, code: 'ACCOUNT_PASSWORD_MISSING_DIGIT' };
  return { ok: true, password };
}

function translatePseudonymPolicyCode(
  code: AccountPseudonymValidationCode,
  translator: ReturnType<typeof createPublicTranslator<AccountMessageKey>>,
): string {
  if (code === 'ACCOUNT_HANDLE_INVALID_LENGTH') return translator.t('pseudonymLength');
  if (code === 'ACCOUNT_HANDLE_RESERVED') return translator.t('pseudonymReserved');
  if (code === 'ACCOUNT_HANDLE_REQUIRED') return translator.t('pseudonymRequired');
  return translator.t('pseudonymFormat');
}

function translatePasswordPolicyCode(
  code: AccountPasswordValidationCode,
  translator: ReturnType<typeof createPublicTranslator<AccountMessageKey>>,
): string {
  if (code === 'ACCOUNT_PASSWORD_REQUIRED') return translator.t('passwordRequired');
  if (code === 'ACCOUNT_PASSWORD_INVALID_LENGTH') return translator.t('passwordLength');
  if (code === 'ACCOUNT_PASSWORD_LATIN_ALNUM_ONLY') return translator.t('passwordLatinOnly');
  if (code === 'ACCOUNT_PASSWORD_MISSING_LOWERCASE') return translator.t('passwordLowercase');
  if (code === 'ACCOUNT_PASSWORD_MISSING_UPPERCASE') return translator.t('passwordUppercase');
  if (code === 'ACCOUNT_PASSWORD_MISSING_DIGIT') return translator.t('passwordDigit');
  return translator.t('passwordRules');
}

function accountMessageError(message: string): Error {
  const error = new Error(message);
  error.name = 'AccountMessageError';
  return error;
}

function localizedHomeFromAccount(accountHref: string): string {
  try {
    const url = new URL(accountHref, window.location.origin);
    return url.pathname.replace(/account\/?$/, '');
  } catch {
    return '/';
  }
}

function isSafeLocalPath(value: string): boolean {
  return value.startsWith('/') && !value.startsWith('//') && !/[\u0000-\u001f]/.test(value);
}

function escapeHtml(value: unknown): string {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function escapeAttr(value: unknown): string {
  return escapeHtml(value).replace(/`/g, '&#96;');
}
